import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  Alert,
  Skeleton,
  IconButton,
  Tooltip,
  TextField,
  Card,
  CardContent,
  CardActions,
  Stack,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import KeyIcon from "@mui/icons-material/Key";
import CreateKeyDialog from "@/components/CreateKeyDialog";
import EmptyState from "@/components/EmptyState";
import {
  getApiKeys,
  createApiKey,
  revokeApiKey,
  renameApiKey,
} from "@/api/dashboard";
import type { ApiKeyItem, ApiKeyCreateResponse } from "@/api/dashboard";
import { extractErrorMessage } from "@/utils/errors";

function KeyCard({
  apiKey,
  onStartRename,
  onRevoke,
  isEditing,
  editValue,
  onEditChange,
  onFinishRename,
  onCancelRename,
}: {
  apiKey: ApiKeyItem;
  onStartRename: () => void;
  onRevoke: () => void;
  isEditing: boolean;
  editValue: string;
  onEditChange: (value: string) => void;
  onFinishRename: () => void;
  onCancelRename: () => void;
}) {
  const revoked = apiKey.revokedAt != null;

  return (
    <Card variant="outlined" sx={{ opacity: revoked ? 0.6 : 1 }}>
      <CardContent sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          {isEditing ? (
            <TextField
              value={editValue}
              onChange={(e) => onEditChange(e.target.value)}
              onBlur={onFinishRename}
              onKeyDown={(e) => {
                if (e.key === "Enter") onFinishRename();
                if (e.key === "Escape") onCancelRename();
              }}
              size="small"
              variant="standard"
              // eslint-disable-next-line jsx-a11y/no-autofocus -- inline-edit UX: focus needed when user clicks rename
              autoFocus
              sx={{ "& input": { fontSize: "1rem", fontWeight: 600 } }}
            />
          ) : (
            <>
              <Typography variant="subtitle2" fontWeight={600}>
                {apiKey.name}
              </Typography>
              {!revoked && (
                <Tooltip title="Rename key">
                  <IconButton
                    size="small"
                    onClick={onStartRename}
                    aria-label={`Rename ${apiKey.name}`}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
          {revoked && (
            <Chip
              label="Revoked"
              color="error"
              size="small"
              variant="outlined"
            />
          )}
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" color="text.secondary">
            {new Date(apiKey.createdAt).toLocaleDateString()}
          </Typography>
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ opacity: revoked ? 0.5 : 1 }}
        >
          <Typography
            variant="body2"
            sx={{
              fontFamily: "monospace",
              fontSize: 13,
              flex: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {apiKey.keyPrefix}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ minWidth: 80, textAlign: "right" }}
          >
            {apiKey.lastUsedAt
              ? `Used ${new Date(apiKey.lastUsedAt).toLocaleDateString()}`
              : "Never used"}
          </Typography>
        </Stack>
      </CardContent>

      {!revoked && (
        <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
          <Button
            size="small"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={onRevoke}
          >
            Revoke
          </Button>
        </CardActions>
      )}
    </Card>
  );
}

export default function ApiKeysPage() {
  const [keys, setKeys] = useState<ApiKeyItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createType, setCreateType] = useState<"live" | "test" | null>(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<ApiKeyItem | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [snackbar, setSnackbar] = useState<string | null>(null);
  const [newKey, setNewKey] = useState<ApiKeyCreateResponse | null>(null);

  const loadKeys = useCallback(async () => {
    try {
      const data = await getApiKeys();
      setKeys(data);
      setError(null);
    } catch (err: unknown) {
      setError(extractErrorMessage(err, "Failed to load API keys"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const handleCreate = async (name: string, type: "live" | "test") => {
    setCreateLoading(true);
    try {
      const result = await createApiKey(name, type);
      setNewKey(result);
      setCreateType(null);
      await loadKeys();
    } catch (err: unknown) {
      setError(extractErrorMessage(err, "Failed to create key"));
    } finally {
      setCreateLoading(false);
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    try {
      await revokeApiKey(revokeTarget.id);
      setRevokeTarget(null);
      await loadKeys();
    } catch (err: unknown) {
      setError(extractErrorMessage(err, "Failed to revoke key"));
    }
  };

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setSnackbar("Copied!");
    } catch {
      setSnackbar("Failed to copy");
    }
  };

  const handleStartRename = (key: ApiKeyItem) => {
    setEditingId(key.id);
    setEditValue(key.name);
  };

  const handleFinishRename = async (key: ApiKeyItem) => {
    const trimmed = editValue.trim();
    setEditingId(null);
    if (!trimmed || trimmed === key.name) return;

    try {
      await renameApiKey(key.id, trimmed);
      await loadKeys();
    } catch (err: unknown) {
      setError(extractErrorMessage(err, "Failed to rename key"));
    }
  };

  if (loading) {
    return (
      <Box>
        <Typography variant="h5" component="h1" gutterBottom>
          API Keys
        </Typography>
        <Skeleton variant="rounded" height={400} />
      </Box>
    );
  }

  const liveKeys = keys.filter((k) => k.type === "live");
  const testKeys = keys.filter((k) => k.type === "test");

  const renderKeyGroup = (
    title: string,
    type: "live" | "test",
    groupKeys: ApiKeyItem[],
    colorLabel: "primary" | "secondary",
  ) => (
    <Box sx={{ mb: 4 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={{ xs: 1.5, sm: 0 }}
        sx={{ mb: 2 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6" component="h2">
            {title}
          </Typography>
          <Chip
            label={type === "live" ? "pk_live_*" : "pk_test_*"}
            size="small"
            color={colorLabel}
            variant="outlined"
          />
        </Stack>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setCreateType(type)}
          sx={{ borderRadius: 24, alignSelf: { xs: "stretch", sm: "auto" } }}
        >
          Create {type === "live" ? "Prod" : "Test"} Key
        </Button>
      </Stack>

      {type === "live" && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Production keys count against your monthly quota.
        </Alert>
      )}
      {type === "test" && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Test keys verify that your API calls are structured correctly. Your
          PDF will not be generated — a generic test PDF is returned instead.
          Test keys do not count against your quota.
        </Alert>
      )}

      <Stack spacing={1.5}>
        {groupKeys.map((key) => (
          <KeyCard
            key={key.id}
            apiKey={key}
            onStartRename={() => handleStartRename(key)}
            onRevoke={() => setRevokeTarget(key)}
            isEditing={editingId === key.id}
            editValue={editValue}
            onEditChange={setEditValue}
            onFinishRename={() => handleFinishRename(key)}
            onCancelRename={() => setEditingId(null)}
          />
        ))}
        {groupKeys.length === 0 && (
          <EmptyState
            icon={
              <KeyIcon aria-hidden="true" fontSize="large" color="disabled" />
            }
            title={`No ${type === "live" ? "production" : "test"} keys yet`}
            description={`Create a ${type === "live" ? "production" : "test"} key to start making requests.`}
          />
        )}
      </Stack>
    </Box>
  );

  return (
    <Box>
      <Typography variant="h5" component="h1" sx={{ mb: 3 }}>
        API Keys
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {renderKeyGroup("Production Keys", "live", liveKeys, "primary")}
      {renderKeyGroup("Test Keys", "test", testKeys, "secondary")}

      <CreateKeyDialog
        open={createType != null}
        keyType={createType ?? "live"}
        onClose={() => setCreateType(null)}
        onSubmit={handleCreate}
        loading={createLoading}
      />

      {/* New key success dialog */}
      <Dialog
        open={!!newKey}
        onClose={() => setNewKey(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Key Created</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Copy this key now. It won&apos;t be shown again.
          </Alert>
          {newKey && (
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 0.5 }}
              >
                {newKey.type === "live" ? "Production" : "Test"} Key —{" "}
                {newKey.name}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "monospace",
                    fontSize: 13,
                    flex: 1,
                    wordBreak: "break-all",
                  }}
                >
                  {newKey.key}
                </Typography>
                <Tooltip title="Copy to clipboard">
                  <IconButton
                    size="small"
                    onClick={() => handleCopy(newKey.key)}
                    aria-label="Copy API key to clipboard"
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setNewKey(null)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revoke confirmation dialog */}
      <Dialog open={!!revokeTarget} onClose={() => setRevokeTarget(null)}>
        <DialogTitle>Revoke Key?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently revoke the{" "}
            {revokeTarget?.type === "live" ? "production" : "test"} key{" "}
            <strong>{revokeTarget?.name}</strong>. Any applications using this
            key will immediately lose access. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeTarget(null)}>Cancel</Button>
          <Button onClick={handleRevoke} color="error" variant="contained">
            Revoke Key
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={2000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}
